import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import apiClient from "@/utils/apiClient";
import { Store, Search, Star, ExternalLink, Mail, Phone, MapPin, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function WellnessMall() {
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [selectedBiz, setSelectedBiz] = useState(null);

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (activeCategory) params.category = activeCategory;
      if (featuredOnly) params.featured_only = true;
      const res = await apiClient.get('/mall/businesses', { params });
      setBusinesses(res.data.businesses);
      setTotal(res.data.total);
      setCategories(res.data.categories || []);
    } catch (err) { console.error('Failed to fetch businesses:', err); }
    finally { setLoading(false); }
  }, [search, activeCategory, featuredOnly]);

  useEffect(() => { fetchBusinesses(); }, [fetchBusinesses]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBusinesses();
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="wellness-mall">
        <div className="mb-8">
          <p className="overline-text mb-2">Discover</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-medium text-spa-moss mb-2">SMAHOO ME.Wellness Mall</h1>
          <p className="text-spa-text-secondary">Explore our curated directory of wellness partners and businesses</p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-spa-text-secondary" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search businesses..." className="pl-9 spa-input" data-testid="mall-search" />
          </div>
          <Button type="submit" className="spa-button-primary" data-testid="mall-search-btn">Search</Button>
        </form>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => { setActiveCategory(""); setFeaturedOnly(false); }} className={`px-3 py-1.5 rounded-full text-sm transition-all ${!activeCategory && !featuredOnly ? "bg-spa-moss text-white" : "bg-spa-muted text-spa-text-secondary hover:bg-spa-border"}`} data-testid="filter-all">All</button>
          <button onClick={() => setFeaturedOnly(!featuredOnly)} className={`px-3 py-1.5 rounded-full text-sm transition-all flex items-center gap-1 ${featuredOnly ? "bg-spa-terracotta text-white" : "bg-spa-muted text-spa-text-secondary hover:bg-spa-border"}`} data-testid="filter-featured">
            <Star className="w-3 h-3" /> Featured
          </button>
          {categories.map(c => (
            <button key={c} onClick={() => setActiveCategory(activeCategory === c ? "" : c)} className={`px-3 py-1.5 rounded-full text-sm transition-all ${activeCategory === c ? "bg-spa-sage text-white" : "bg-spa-muted text-spa-text-secondary hover:bg-spa-border"}`} data-testid={`cat-${c.toLowerCase().replace(/\s/g, '-')}`}>
              {c}
            </button>
          ))}
        </div>

        <p className="text-spa-text-secondary text-sm mb-4">{total} businesses found</p>

        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-spa-terracotta border-t-transparent rounded-full animate-spin" /></div>
        ) : businesses.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {businesses.map(b => (
              <div key={b.business_id} className={`spa-card cursor-pointer hover:shadow-lg transition-all ${b.is_featured ? "ring-2 ring-spa-terracotta/30" : ""}`} onClick={() => setSelectedBiz(b)} data-testid={`mall-biz-${b.business_id}`}>
                {b.is_featured && (
                  <div className="flex items-center gap-1 mb-3"><Star className="w-4 h-4 text-spa-terracotta fill-spa-terracotta" /><span className="text-spa-terracotta text-xs font-medium">Featured</span></div>
                )}
                <span className="px-2 py-0.5 bg-spa-sage/10 text-spa-sage rounded-full text-xs mb-3 inline-block">{b.category}</span>
                <h3 className="font-serif text-lg font-medium text-spa-moss mb-2">{b.name}</h3>
                <p className="text-spa-text-secondary text-sm line-clamp-3">{b.description}</p>
                {b.website && (
                  <div className="flex items-center gap-1 mt-3 text-spa-terracotta text-sm">
                    <ExternalLink className="w-3 h-3" /> Visit Website
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="spa-card p-12 text-center">
            <Store className="w-12 h-12 text-spa-border mx-auto mb-4" />
            <p className="text-spa-text-secondary">No businesses match your search</p>
          </div>
        )}

        {/* Business Detail Modal */}
        {selectedBiz && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedBiz(null)}>
            <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="biz-detail-modal">
              <div className="flex items-start justify-between mb-4">
                <div>
                  {selectedBiz.is_featured && <div className="flex items-center gap-1 mb-2"><Star className="w-4 h-4 text-spa-terracotta fill-spa-terracotta" /><span className="text-spa-terracotta text-xs">Featured Partner</span></div>}
                  <span className="px-2 py-0.5 bg-spa-sage/10 text-spa-sage rounded-full text-xs">{selectedBiz.category}</span>
                </div>
                <button onClick={() => setSelectedBiz(null)} className="p-2 hover:bg-spa-muted rounded-xl"><X className="w-5 h-5 text-spa-text-secondary" /></button>
              </div>
              <h2 className="font-serif text-2xl font-medium text-spa-moss mb-3">{selectedBiz.name}</h2>
              <p className="text-spa-text-secondary mb-4">{selectedBiz.description}</p>
              <div className="space-y-2 text-sm">
                {selectedBiz.website && (
                  <a href={selectedBiz.website.startsWith("http") ? selectedBiz.website : `https://${selectedBiz.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-spa-terracotta hover:underline">
                    <ExternalLink className="w-4 h-4" /> {selectedBiz.website}
                  </a>
                )}
                {selectedBiz.email && <div className="flex items-center gap-2 text-spa-text-secondary"><Mail className="w-4 h-4" /> {selectedBiz.email}</div>}
                {selectedBiz.phone && <div className="flex items-center gap-2 text-spa-text-secondary"><Phone className="w-4 h-4" /> {selectedBiz.phone}</div>}
                {selectedBiz.address && <div className="flex items-center gap-2 text-spa-text-secondary"><MapPin className="w-4 h-4" /> {selectedBiz.address}</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
